import { useState, useCallback } from 'react';

// Define the cache key structure
export type CacheKey = {
  strategySlug: string;
  ticker: string;
  expirationKey: string;
};

// Define the cache value structure
export type StrategyCalculationData = {
  strategyValues: any;
  maxProfitLoss?: { maxProfit: number; maxLoss: number };
  breakEvenPoints?: number[];
  probabilityData: any[];
  payoffGraph: any[];
  payoffTable?: any[][];
  spot: number;
  normalizedProfitFactor: number;
  loading: boolean;
};

// Generate a string key for the Map
export const getCacheKey = (
  strategySlug: string,
  ticker: string,
  expirationKey: string,
): string => {
  return `${strategySlug}:${ticker}:${expirationKey}`;
};

// Hook for managing strategy calculations cache
export const useStrategyCalculationsCache = () => {
  const [cache, setCache] = useState<Map<string, StrategyCalculationData>>(new Map());

  // Set a value in the cache
  const setCacheValue = useCallback(
    (
      strategySlug: string,
      ticker: string,
      expirationKey: string,
      data: StrategyCalculationData,
    ) => {
      const key = getCacheKey(strategySlug, ticker, expirationKey);
      setCache((prevCache) => {
        const newCache = new Map(prevCache);
        newCache.set(key, data);
        return newCache;
      });
    },
    [],
  );
  // Get a value from the cache
  const getCacheValue = useCallback(
    (
      strategySlug: string,
      ticker: string,
      expirationKey: string,
    ): StrategyCalculationData | undefined => {
      const key = getCacheKey(strategySlug, ticker, expirationKey);
      return cache.get(key);
    },
    [cache],
  );

  // Check if a value exists in the cache
  const hasCacheValue = useCallback(
    (strategySlug: string, ticker: string, expirationKey: string): boolean => {
      const key = getCacheKey(strategySlug, ticker, expirationKey);
      return cache.has(key);
    },
    [cache],
  );

  // Clear the cache when ticker or expiration changes
  const clearCache = useCallback(() => {
    setCache(new Map());
  }, []);

  // Get all cached values
  const getAllCacheValues = useCallback((): Map<string, StrategyCalculationData> => {
    return cache;
  }, [cache]);

  return {
    setCacheValue,
    getCacheValue,
    hasCacheValue,
    getAllCacheValues,
    clearCache,
    cacheSize: cache.size,
    getCacheKey,
  };
};

export type StrategyCalculationsCache = ReturnType<typeof useStrategyCalculationsCache>;
